import { defineComponent as _defineComponent } from 'vue'
import { GuestChargingService } from "@/services/guest-charging-service"
import { PriceService } from "@/services/price-service"
import ConfirmModal from "@/components/ui/ConfirmModal"
import FeedbackModal from "@/components/ui/FeedbackModal"
import config from "../../config/index"
import PageHeader from "../ui/PageHeader.vue"
import ButtonComponent from "../ui/ButtonComponent.vue"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDictionaryStore } from "../../store/dictionary"
import { storeToRefs } from "pinia"
import { chargerTypes, chargerTypesFromBackend } from "@/models/common"
import type { Charger } from "@/models/common"
import { ChargingStatus } from "@/models/enums"

interface ChargingLayoutProps {
  selectedCharger?: Charger
  chargingStartedAt?: Date
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChargingLayout',
  props: {
    selectedCharger: { default: () => ({ address: "" }) },
    chargingStartedAt: { default: () => new Date() }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props

const dictionaryStore = useDictionaryStore()
const { dynamicConfig } = storeToRefs(dictionaryStore)
const i18n = useI18n()
const router = useRouter()

const feedbackStopMainTitle = ref("")
const feedbackStopSubTitle = ref("")
const feedbackStopSuccess = ref(false)
const stopButtonVisible = ref(false)
const pollingIntervalId = ref<ReturnType<typeof setInterval> | null>(null)
const chargingInfo = ref(
  "chargingPage.chargingLayout.chargingInfo.startCommandAccepted"
)
const isConfirmModalActive = ref(false)
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)
const endTime = ref<Date | null>(null)

const price = computed(() => {
  return PriceService.getPriceLabel(
    props.selectedCharger.connector?.price,
    i18n
  )
})

const connectorTypeSlug = computed(() => {
  const connectorType = props.selectedCharger.connector?.connectorType

  return connectorType ? chargerTypesFromBackend[connectorType] : "type2"
})

const connectorTypeName = computed(() => {
  return chargerTypes[connectorTypeSlug.value]
    ? chargerTypes[connectorTypeSlug.value]
    : chargerTypes.type2
})

const stopClick = () => {
  isConfirmModalActive.value = true
}

const feedbackStopOk = () => {
  feedbackStopSuccess.value = true
  feedbackStopMainTitle.value = "chargingPage.chargingLayout.stopOk.mainTitle"
  feedbackStopSubTitle.value = "chargingPage.chargingLayout.stopOk.subTitle"
  isFeedbackModalActive.value = true
}

const afterSuccesfulStop = () => {
  const url = dynamicConfig?.value?.redirectUrl
  if (url) {
    window.location.href = url
  } else {
    router.push({ name: "LandingPage" })
  }
}

const feedbackStopError = (error: any) => {
  console.error("ChargingLayout feedbackStopError")

  feedbackStopSuccess.value = false

  if (error.messageCode === "guestCharging.session.alreadyStopped") {
    feedbackStopMainTitle.value =
      "errors.chargingErrors.alreadyStoppedError.mainTitle"
    feedbackStopSubTitle.value =
      "errors.chargingErrors.alreadyStoppedError.subTitle"
  } else {
    feedbackStopMainTitle.value = "errors.defaultError.mainTitle"
    feedbackStopSubTitle.value = "errors.defaultError.subTitle"
  }

  isFeedbackModalActive.value = true
}

const confirmStopCharge = async () => {
  endTime.value = new Date()
  submitIsLoading.value = true
  isConfirmModalActive.value = false

  try {
    const evseId = props.selectedCharger.connector?.evseId

    if (evseId) {
      await GuestChargingService.stopCharging(evseId)
    }
    feedbackStopOk()
    setTimeout(() => {
      afterSuccesfulStop()
    }, 5000)
  } catch (e) {
    console.error(e)
    feedbackStopError(e)
  } finally {
    submitIsLoading.value = false
  }
}

const getStatus = async () => {
  try {
    const { data } = await GuestChargingService.getChargingStatus()
    const { chargingStatus } = data

    const actions: Partial<Record<ChargingStatus, () => void>> = {
      [ChargingStatus.STOPPED]: () => afterSuccesfulStop(),
      [ChargingStatus.STARTED]: () => {
        stopButtonVisible.value = true
        chargingInfo.value = "chargingPage.chargingLayout.chargingInfo.started"
      },
      [ChargingStatus.INVALID]: () => {
        feedbackStopMainTitle.value =
          "errors.chargingStatusErrors.invalid.mainTitle"
        feedbackStopSubTitle.value =
          "errors.chargingStatusErrors.invalid.subTitle"
        isFeedbackModalActive.value = true
      },
      [ChargingStatus.PENDING]: () => {
        chargingInfo.value = "chargingPage.chargingLayout.chargingInfo.pending"
      },
      [ChargingStatus.START_COMMAND_ACCEPTED]: () => {
        chargingInfo.value =
          "chargingPage.chargingLayout.chargingInfo.startCommandAccepted"
      }
    }

    actions[chargingStatus as ChargingStatus]?.()
  } catch (error) {
    console.error(error)
  }
}

const pollStatus = () => {
  pollingIntervalId.value = setInterval(() => {
    getStatus()
  }, 10000)
}

const handleFeedbackModalOkClick = () => {
  if (!feedbackStopSuccess.value) {
    afterSuccesfulStop()
  }
  isFeedbackModalActive.value = false
}

const handleConfirmModalCancel = () => {
  isConfirmModalActive.value = false
}

const displayTime = (date: Date) => {
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`
}

onMounted(() => {
  getStatus()
  pollStatus()
})

onBeforeUnmount(() => {
  if (pollingIntervalId.value) {
    clearInterval(pollingIntervalId.value)
  }
})

const __returned__ = { props, dictionaryStore, dynamicConfig, i18n, router, feedbackStopMainTitle, feedbackStopSubTitle, feedbackStopSuccess, stopButtonVisible, pollingIntervalId, chargingInfo, isConfirmModalActive, isFeedbackModalActive, submitIsLoading, endTime, price, connectorTypeSlug, connectorTypeName, stopClick, feedbackStopOk, afterSuccesfulStop, feedbackStopError, confirmStopCharge, getStatus, pollStatus, handleFeedbackModalOkClick, handleConfirmModalCancel, displayTime, get GuestChargingService() { return GuestChargingService }, get PriceService() { return PriceService }, get ConfirmModal() { return ConfirmModal }, get FeedbackModal() { return FeedbackModal }, get config() { return config }, PageHeader, ButtonComponent, computed, onBeforeUnmount, onMounted, ref, get useI18n() { return useI18n }, get useRouter() { return useRouter }, get useDictionaryStore() { return useDictionaryStore }, get storeToRefs() { return storeToRefs }, get chargerTypes() { return chargerTypes }, get chargerTypesFromBackend() { return chargerTypesFromBackend }, get ChargingStatus() { return ChargingStatus } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})